//
// Copyright (c) 2021 Astute Global, Inc.
//

import i18next from "i18n/i18n";
import { ButtonView, clickOutsideHandler, ContextualBalloon, Plugin } from "ckeditor5";

import responseAttributeIcon from "img/svg/add_attribute.svg";
import ResponseAttributeView from "./ui/responseAttributeView";

export default class ResponseAttributeUi extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [ContextualBalloon];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "ResponseAttributeUi";
  }

  init() {
    this._addToolbarButton();
    this.balloon = this.editor.plugins.get(ContextualBalloon);
  }

  get _isResponseAttributeViewInPanel() {
    return this.balloon.hasView(this.responseAttributeView);
  }

  _addResponseAttributeView() {
    this.responseAttributeView = this._createResponseAttributeView();
    const view = this.editor.editing.view;
    const viewDocument = view.document;
    this.responseAttributeView.disableCssTransitions();
    this.balloon.add({
      view: this.responseAttributeView,
      position: {
        target: () => {
          return view.domConverter.viewRangeToDom(viewDocument.selection.getFirstRange());
        },
      },
    });
    if (this.balloon.visibleView == this.responseAttributeView) {
      this.responseAttributeView.focus();
    }
    this.responseAttributeView.enableCssTransitions();
  }

  _addToolbarButton() {
    const editor = this.editor;
    editor.ui.componentFactory.add("responseAttribute", (locale) => {
      const command = editor.commands.get("responseAttribute");
      const button = new ButtonView(locale);
      button.set({
        isEnabled: true,
        label: i18next.t("ckeditor.attribute.buttonName"),
        icon: responseAttributeIcon,
        tooltip: true,
      });
      button.bind("isEnabled").to(command);
      this.listenTo(button, "execute", () => this._showUi());
      return button;
    });
  }

  _cancelUi() {
    this.editor.commands.get("responseAttribute").refresh();
    this._closeResponseAttributeView();
  }

  _closeResponseAttributeView() {
    if (this._isResponseAttributeViewInPanel) {
      this.balloon.remove(this.responseAttributeView);
      this.responseAttributeView.destroy();
      this.editor.editing.view.focus();
    }
  }

  _createResponseAttributeView() {
    const editor = this.editor;
    const command = editor.commands.get("responseAttribute");
    const responseAttributeView = new ResponseAttributeView(editor, command);
    responseAttributeView.set({
      responseId: editor.config.get("responseId"),
    });
    responseAttributeView.on("submit", () => {
      editor.execute("responseAttribute", {
        insertType: responseAttributeView.insertType,
        attributeId: responseAttributeView.attributeId,
        attributeName: responseAttributeView.attributeName,
      });
      this._closeResponseAttributeView();
    });
    responseAttributeView.on("cancel", () => {
      this._cancelUi();
    });
    clickOutsideHandler({
      emitter: responseAttributeView,
      activator: () => this._isResponseAttributeViewInPanel,
      contextElements: [this.balloon.view.element],
      callback: () => this._cancelUi(),
    });
    return responseAttributeView;
  }

  _showUi() {
    this._addResponseAttributeView();
    this.balloon.showStack("main");
  }
}
